import type {
  ElectronicInvoice,
  InvoiceLine,
  InvoiceListSummary,
  InvoiceReceiverSnapshot,
  InvoiceTotals,
  RecibidasListSummary,
} from "@fiscalo/shared-types";
import type { AxiosResponseHeaders } from "axios";

import { apiClient } from "@/services/apiClient";

/** Related note as returned by GET /invoices/:id (mapRelatedInvoice). */
export interface RelatedNote {
  id: string;
  cfeType: string;
  number: number;
  series: string | null;
  issueDate: string;
  total: number;
  status: string;
  dgiStatus?: string;
}

/** Enriched invoice detail when original is accepted base invoice (101/111/121). */
export interface InvoiceDetailEnriched extends ElectronicInvoice {
  originalInvoiceTotal?: number;
  totalCredited?: number;
  totalDebited?: number;
  remainingBalance?: number;
  canIssueCreditNote?: boolean;
  canIssueDebitNote?: boolean;
  relatedCreditNotes?: RelatedNote[];
  relatedDebitNotes?: RelatedNote[];
  /** Present when domestic invoice was entered in USD and converted to UYU. */
  originalCurrency?: string;
  /** Present when domestic invoice was converted from USD to UYU. */
  originalTotals?: InvoiceTotals;
  /**
   * The marca it was issued under, or null for the legal entity's own
   * branding. Lives HERE rather than on ElectronicInvoice: that type is the
   * domain object packages/dgi maps into a CFE, and a brand must never be
   * reachable from there.
   */
  brandId?: string | null;
  /** This invoice's own adenda text (not the issued snapshot). */
  adendaText?: string | null;
  /** Whether this invoice was issued without the standing adenda. */
  adendaSuppressDefault?: boolean;
  /** Name of that marca, resolved server-side so the chip can never disagree
   *  with the identity the invoice was issued under. */
  brandName?: string | null;
}

/** Request body for POST /invoices/:id/credit-note. Matches backend CreateCreditNoteDto. */
export interface CreateCreditNoteRequest {
  series?: string;
  currency: "UYU" | "USD";
  issueDate: string;
  receiverSnapshot: Partial<InvoiceReceiverSnapshot>;
  exportHeader?: CreateInvoiceExportHeader;
  lines: InvoiceLine[];
  totals: InvoiceTotals;
  /** Global reference (DGI IndGlobal=1) + mandatory reason — E12 escape. */
  referenceGlobal?: boolean;
  referenceReason?: string;
}

/** Request body for POST /invoices/:id/debit-note. Matches backend CreateDebitNoteDto. */
export interface CreateDebitNoteRequest {
  series?: string;
  currency: "UYU" | "USD";
  issueDate: string;
  receiverSnapshot: Partial<InvoiceReceiverSnapshot>;
  exportHeader?: CreateInvoiceExportHeader;
  lines: InvoiceLine[];
  totals: InvoiceTotals;
}

export interface ListInvoicesParams {
  page?: number;
  limit?: number;
  status?: string;
  period?: string;
  dateFrom?: string;
  dateTo?: string;
  customerId?: string;
  currency?: string;
  createdByUserId?: string;
}

export interface ListInvoicesResponse {
  data: ElectronicInvoice[];
  total: number;
  summary: InvoiceListSummary;
}

/** GET /invoices/customer-summary — activity for one client. */
export interface CustomerInvoiceSummaryResponse {
  invoiceCountYear: number;
  invoiceCountAll: number;
  salesAcceptedYear: { UYU: number; USD: number };
  salesAcceptedAll: { UYU: number; USD: number };
  matchedCatalogSalesYear: Array<{
    catalogItemId: string;
    label: string;
    amountUYU: number;
    amountUSD: number;
  }>;
  unmatchedLineSalesYear: Array<{
    description: string;
    amountUYU: number;
    amountUSD: number;
  }>;
  matchedCatalogSalesAll: Array<{
    catalogItemId: string;
    label: string;
    amountUYU: number;
    amountUSD: number;
  }>;
  unmatchedLineSalesAll: Array<{
    description: string;
    amountUYU: number;
    amountUSD: number;
  }>;
  adjustmentsYear: Array<{
    cfeType: string;
    description: string;
    amountUYU: number;
    amountUSD: number;
  }>;
  adjustmentsAll: Array<{
    cfeType: string;
    description: string;
    amountUYU: number;
    amountUSD: number;
  }>;
}

export function getCustomerInvoiceSummary(
  customerId: string
): Promise<CustomerInvoiceSummaryResponse> {
  return apiClient
    .get<CustomerInvoiceSummaryResponse>("/invoices/customer-summary", {
      params: { customerId },
    })
    .then((res) => res.data);
}

/** Single item from GET /invoices/recibidas (CFEs received by the company). */
export interface RecibidaItem {
  id: string;
  tipoCFE: string;
  serie: string;
  nro: number;
  estado: string;
  fechaHora: string;
  rucEmisor: string;
  /** Emitter legal name from stored data (XML import or DGI at sync time). No DGI call on list. */
  legalName?: string;
  moneda: string;
  /** Payment method (contado/credito) when available (e.g. from XML import). Null when synced from DGI. */
  formaPago?: string | null;
  /** True when a CFE XML file was imported for this row; DGI sync does not store XML. */
  hasRawXml?: boolean;
  totalNeto: number;
  totalIVA: number;
  montoTotal: number;
  totalRetenido: number;
  /** Receptor's accept/reject decision (AE/RE) — null when undecided. */
  myAckCfeEstado?: string | null;
  myAckCfeDecidedAt?: string | null;
  /** True iff this row needs an ACKCFE decision (EnvioCFE-origin). */
  isEligibleForAckCfe?: boolean;
  /** Fiscalo-layer supplier id (resolved at ingest). Null when the
   *  rucEmisor couldn't be matched (e.g. malformed XML). */
  supplierId?: string | null;
  /** ISO timestamp this row was first ingested; drives 20-día countdown. */
  createdAt?: string | null;
}

export interface ListRecibidasParams {
  dateFrom: string;
  dateTo: string;
  page?: number;
  limit?: number;
  currency?: string;
  /** When true, exclude e-Resguardo (182) for IVA compras detail. */
  forIvaCompras?: boolean;
  /** When true, return only EnvioCFE-origin rows with no receptor
   *  decision yet — date range is bypassed server-side so older
   *  overdue rows surface. */
  pendingAck?: boolean;
  /** When set, filter recibidas to this Fiscalo-layer supplier. */
  supplierId?: string;
}

export interface ListRecibidasResponse {
  data: RecibidaItem[];
  total: number;
}

/** Export header for CFE 121. Backend defaults FOB / viaTransp 1 if omitted. */
export interface CreateInvoiceExportHeader {
  clauVenta: string;
  viaTransp: 1 | 2 | 3 | 9;
  modVenta?: number;
}

/** Request body for POST /invoices. Matches backend CreateInvoiceDto shape. */
export interface CreateInvoiceRequest {
  cfeType?: string;
  series?: string;
  currency: "UYU" | "USD";
  issueDate: string;
  receiverSnapshot: Partial<InvoiceReceiverSnapshot>;
  lines: InvoiceLine[];
  totals: InvoiceTotals;
  exportHeader?: CreateInvoiceExportHeader;
  /** Payment method: contado (spot) or credito (credit). Default contado. */
  paymentMethod?: "contado" | "credito";
  /** Client id when the invoice is created for a known client. Stored in payload for filtering. */
  customerId?: string;
  /** Per-invoice override of the client's autoSendInvoiceEmail default — mainly for scheduled drafts. */
  autoSendEmailOverride?: boolean;
  /** When domestic invoice was entered in USD and converted to UYU. */
  exchangeRate?: number;
  originalCurrency?: string;
  originalTotals?: InvoiceTotals;
  /** Re-issue link: UUID of a rejected invoice this one replaces. Backend
   * inherits the source's notificationUrl so the original API integrator still
   * receives invoice.accepted/rejected for the manually re-issued version. */
  sourceInvoiceId?: string;
  /**
   * The marca to issue under. Omitted lets the server decide (source invoice's
   * brand, then the client's default, then the legal entity's own branding).
   * Presentation only — the CFE always carries the company's RUT and razón
   * social.
   */
  brandId?: string;
  /**
   * This invoice's adenda (CFE Zona J). APPENDED to the tenant's standing
   * adenda, not a replacement — use adendaSuppressDefault to issue without it.
   * No length cap: DGI declares Adenda untyped, and a long one paginates.
   */
  adendaText?: string;
  /** Drop the standing (marca, else company) adenda for this invoice only. */
  adendaSuppressDefault?: boolean;
}

export function listInvoices(
  params: ListInvoicesParams = {}
): Promise<ListInvoicesResponse> {
  const { page = 1, limit = 10, status, period, dateFrom, dateTo, customerId, currency, createdByUserId } = params;
  return apiClient
    .get<ListInvoicesResponse>("/invoices", {
      params: {
        page,
        limit,
        ...(status ? { status } : {}),
        ...(period ? { period } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(customerId ? { customerId } : {}),
        ...(currency ? { currency } : {}),
        ...(createdByUserId ? { createdByUserId } : {}),
      },
    })
    .then((res) => res.data);
}

/**
 * Download the issued-invoices list as CSV honouring the current filters
 * (every matching row, not just the visible page). Streams the file via a
 * blob download — same pattern as reportService.downloadSalesCsv.
 */
export async function exportInvoicesCsv(
  params: Omit<ListInvoicesParams, "page" | "limit"> = {}
): Promise<void> {
  const { status, period, dateFrom, dateTo, customerId, currency, createdByUserId } = params;
  const res = await apiClient.get("/invoices/export/csv", {
    params: {
      ...(status ? { status } : {}),
      ...(period ? { period } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(customerId ? { customerId } : {}),
      ...(currency ? { currency } : {}),
      ...(createdByUserId ? { createdByUserId } : {}),
    },
    responseType: "blob",
  });
  const disposition = res.headers["content-disposition"] ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? `facturas_${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

/** What the ZIP export actually managed to include. */
export interface XmlExportCounts {
  exported: number;
  /** Rows with no XML available — historical imports we never submitted. */
  skipped: number;
  /** Matched the filter but is not an exportable document: DGI never accepted
   *  it, or it was never reported in an accepted reporte diario. */
  excluded: number;
}

/**
 * Download the CFE XMLs for the current view as a ZIP, honouring the same
 * filters as exportInvoicesCsv so the two exports describe the same rows.
 *
 * Returns the counts rather than toasting here: a partial export must be
 * reported to the user, and only the caller knows how to phrase it.
 */
export async function exportInvoicesXmlZip(
  params: Omit<ListInvoicesParams, "page" | "limit"> = {}
): Promise<XmlExportCounts> {
  const { status, period, dateFrom, dateTo, customerId, currency, createdByUserId } = params;
  const res = await apiClient.get("/invoices/export/emitted-xml", {
    params: {
      ...(status ? { status } : {}),
      ...(period ? { period } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(customerId ? { customerId } : {}),
      ...(currency ? { currency } : {}),
      ...(createdByUserId ? { createdByUserId } : {}),
    },
    responseType: "blob",
  });

  const num = (h: unknown): number => {
    const n = Number(h);
    return Number.isFinite(n) ? n : 0;
  };
  const counts: XmlExportCounts = {
    exported: num(res.headers["x-export-exported"]),
    skipped: num(res.headers["x-export-skipped"]),
    excluded: num(res.headers["x-export-excluded"]),
  };

  // Nothing to download — don't hand the user an empty ZIP that looks like success.
  if (counts.exported === 0) return counts;

  const disposition = res.headers["content-disposition"] ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? `cfe_xml_${new Date().toISOString().slice(0, 10)}.zip`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
  return counts;
}

/**
 * Download the received-invoices list as CSV honouring the current filters
 * (every matching row, not just the visible page). Blob download — same
 * pattern as exportInvoicesCsv.
 */
/**
 * Download the received-invoices list as CSV honouring the current filters
 * (every matching row, not just the visible page). Blob download — same
 * pattern as exportInvoicesCsv.
 */
/**
 * Download the RECEIVED CFE XMLs for the current view as a ZIP, honouring the
 * same filters as exportRecibidasCsv.
 *
 * Returns counts rather than toasting: rows with no supplier XML are a normal
 * outcome (DGI sync reports the comprobante but not the signed file), and only
 * the caller knows how to phrase that.
 */
export async function exportRecibidasXmlZip(
  params: Omit<ListRecibidasParams, "page" | "limit">
): Promise<{ exported: number; skipped: number }> {
  const { dateFrom, dateTo, currency, forIvaCompras, pendingAck, supplierId } = params;
  const res = await apiClient.get("/invoices/recibidas/export/xml", {
    params: {
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(currency ? { currency } : {}),
      ...(forIvaCompras ? { forIvaCompras: "true" } : {}),
      ...(pendingAck ? { pendingAck: "true" } : {}),
      ...(supplierId ? { supplierId } : {}),
    },
    responseType: "blob",
  });

  const num = (h: unknown): number => {
    const n = Number(h);
    return Number.isFinite(n) ? n : 0;
  };
  const counts = {
    exported: num(res.headers["x-export-exported"]),
    skipped: num(res.headers["x-export-skipped"]),
  };
  // Nothing to download — never hand back an empty ZIP that looks like success.
  if (counts.exported === 0) return counts;

  const disposition = res.headers["content-disposition"] ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? `cfe_recibidos_${new Date().toISOString().slice(0, 10)}.zip`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
  return counts;
}

export async function exportRecibidasCsv(
  params: Omit<ListRecibidasParams, "page" | "limit">
): Promise<void> {
  const { dateFrom, dateTo, currency, forIvaCompras, pendingAck, supplierId } = params;
  const res = await apiClient.get("/invoices/recibidas/export/csv", {
    params: {
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(currency ? { currency } : {}),
      ...(forIvaCompras ? { forIvaCompras: "true" } : {}),
      ...(pendingAck ? { pendingAck: "true" } : {}),
      ...(supplierId ? { supplierId } : {}),
    },
    responseType: "blob",
  });
  const disposition = res.headers["content-disposition"] ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? `recibidas_${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

export function listRecibidas(
  params: ListRecibidasParams
): Promise<ListRecibidasResponse> {
  const { dateFrom, dateTo, page = 1, limit = 20, currency, forIvaCompras, pendingAck, supplierId } = params;
  return apiClient
    .get<ListRecibidasResponse>("/invoices/recibidas", {
      params: {
        dateFrom,
        dateTo,
        page,
        limit,
        ...(currency ? { currency } : {}),
        ...(forIvaCompras ? { forIvaCompras: "true" } : {}),
        ...(pendingAck ? { pendingAck: "true" } : {}),
        ...(supplierId ? { supplierId } : {}),
      },
    })
    .then((res) => res.data);
}

export interface RecibidasSummaryParams {
  dateFrom?: string;
  dateTo?: string;
  currency?: string;
  forIvaCompras?: boolean;
  pendingAck?: boolean;
  supplierId?: string;
}

export function getRecibidasSummary(
  params: RecibidasSummaryParams = {}
): Promise<RecibidasListSummary> {
  const { dateFrom, dateTo, currency, forIvaCompras, pendingAck, supplierId } = params;
  return apiClient
    .get<RecibidasListSummary>("/invoices/recibidas/summary", {
      params: {
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(currency ? { currency } : {}),
        ...(forIvaCompras ? { forIvaCompras: "true" } : {}),
        ...(pendingAck ? { pendingAck: "true" } : {}),
        ...(supplierId ? { supplierId } : {}),
      },
    })
    .then((res) => res.data);
}

export interface RecibidaAckMotivo {
  code: string;
  description: string;
  detail?: string;
}

/** Single received invoice detail (from GET /invoices/recibidas/:id). */
export interface RecibidaDetail extends RecibidaItem {
  acuseSentAt?: string | null;
  /** Terminal reason we stopped trying to send acuse (e.g. 'no_emisor_email'). */
  acuseSkippedReason?: string | null;
  /** Gmail message id of the EnvioCFE email this row arrived from. Null = sync/upload origin, never eligible for acuse. */
  envioCfeMessageId?: string | null;
  /** Receptor's business-layer decision (AE/RE) or null when undecided. */
  myAckCfeEstado?: string | null;
  myAckCfeDecidedAt?: string | null;
  /** UserId of the decider; null when auto-accepted by the day-19 cron. */
  myAckCfeDecidedByUserId?: string | null;
  myAckCfeMotivos?: RecibidaAckMotivo[] | null;
  myAckCfeSentAt?: string | null;
  /** ISO timestamp this row was first ingested; drives the 20-día countdown. */
  createdAt?: string | null;
  /** True iff this row needs an ACKCFE decision (EnvioCFE-origin). */
  isEligibleForAckCfe?: boolean;
  payload?: {
    items?: Array<{ nomItem?: string; cantidad?: number; precioUnitario?: number; montoItem?: number }>;
    referencias?: Array<{ tpoDocRef?: string; serie?: string; nroCFERef?: number }>;
    emisor?: { rucEmisor?: string; rznSoc?: string; domFiscal?: string };
    receptor?: { docRecep?: string; rznSocRecep?: string; dirRecep?: string };
    totales?: { tpoMoneda?: string; mntTotal?: number; mntPagar?: number; montoNF?: number };
    idDoc?: { tipoCFE?: string; serie?: string; nro?: number; fchEmis?: string };
  } | null;
}

export function getRecibidaById(id: string): Promise<RecibidaDetail> {
  return apiClient.get<RecibidaDetail>(`/invoices/recibidas/${id}`).then((res) => res.data);
}

export interface RecibidasPendingAckCount {
  total: number;
  dueSoonCount: number;
  overdueCount: number;
  oldestCreatedAt: string | null;
}

/** Lightweight count for the home banner. */
export function getRecibidasPendingAckCount(): Promise<RecibidasPendingAckCount> {
  return apiClient
    .get<RecibidasPendingAckCount>("/invoices/recibidas/pending-ack-count")
    .then((res) => res.data);
}

export interface RecibidaDecisionResponse {
  estado: string;
  decidedAt: string;
  alreadyDecided: boolean;
  /** Populated only on the user's first AE accept from this emisor
   *  RUT (and only when the RUT isn't already on the allowlist).
   *  UI uses this to ask "always auto-accept from {{legalName}}?". */
  promptAutoAccept: { rucEmisor: string; legalName: string | null } | null;
}

export function acceptRecibida(
  id: string,
): Promise<RecibidaDecisionResponse> {
  return apiClient
    .post<RecibidaDecisionResponse>(`/invoices/recibidas/${id}/accept`)
    .then((res) => res.data);
}

export function rejectRecibida(
  id: string,
  motivos: RecibidaAckMotivo[],
): Promise<RecibidaDecisionResponse> {
  return apiClient
    .post<RecibidaDecisionResponse>(
      `/invoices/recibidas/${id}/reject`,
      { motivos },
    )
    .then((res) => res.data);
}

export interface AutoAcceptEmisorRuc {
  id: string;
  rucEmisor: string;
  legalName: string | null;
  createdAt: string;
}

export function listAutoAcceptRucs(): Promise<AutoAcceptEmisorRuc[]> {
  return apiClient
    .get<AutoAcceptEmisorRuc[]>("/invoices/recibidas/auto-accept-rucs")
    .then((res) => res.data);
}

export function addAutoAcceptRuc(
  rucEmisor: string,
  legalName?: string | null,
): Promise<{ id: string; alreadyExisted: boolean; backfilledCount: number }> {
  return apiClient
    .post<{ id: string; alreadyExisted: boolean; backfilledCount: number }>(
      "/invoices/recibidas/auto-accept-rucs",
      { rucEmisor, legalName: legalName ?? null },
    )
    .then((res) => res.data);
}

export function removeAutoAcceptRuc(id: string): Promise<void> {
  return apiClient
    .delete(`/invoices/recibidas/auto-accept-rucs/${id}`)
    .then(() => undefined);
}

export function getInvoice(id: string): Promise<InvoiceDetailEnriched> {
  return apiClient
    .get<InvoiceDetailEnriched>(`/invoices/${id}`)
    .then((res) => res.data);
}

export function dismissInvoiceRejection(
  id: string,
): Promise<{ dismissedAt: string }> {
  return apiClient
    .post<{ dismissedAt: string }>(`/invoices/${id}/dismiss-rejection`)
    .then((res) => res.data);
}

/** Anular a CFE número — reported as anulado in the Reporte Diario. 409 if DGI-accepted. */
export function annulInvoice(
  id: string,
  reason?: string,
): Promise<{ annulledAt: string; reason: string | null }> {
  return apiClient
    .post<{ annulledAt: string; reason: string | null }>(`/invoices/${id}/annul`, { reason })
    .then((res) => res.data);
}

/** Bulk-dismiss every currently-rejected, unresolved rechazo for the active tenant. */
export function dismissAllInvoiceRejections(): Promise<{ dismissed: number; dismissedAt: string }> {
  return apiClient
    .post<{ dismissed: number; dismissedAt: string }>("/invoices/dismiss-all-rejections")
    .then((res) => res.data);
}

export function createInvoice(
  payload: CreateInvoiceRequest
): Promise<ElectronicInvoice> {
  return apiClient
    .post<ElectronicInvoice>("/invoices", payload)
    .then((res) => res.data);
}

export function createDraftInvoice(
  payload: CreateInvoiceRequest
): Promise<ElectronicInvoice> {
  return apiClient
    .post<ElectronicInvoice>("/invoices/draft", payload)
    .then((res) => res.data);
}

export function sendDraftInvoice(id: string): Promise<ElectronicInvoice> {
  return apiClient
    .post<ElectronicInvoice>(`/invoices/${id}/send`)
    .then((res) => res.data);
}

/** sendAt: full ISO 8601 datetime (exact instant, not just a date). Also used to reschedule an already-scheduled draft. */
export function scheduleInvoiceSend(
  id: string,
  sendAt: string
): Promise<ElectronicInvoice> {
  return apiClient
    .post<ElectronicInvoice>(`/invoices/${id}/schedule`, { sendAt })
    .then((res) => res.data);
}

export function cancelScheduledInvoiceSend(id: string): Promise<void> {
  return apiClient.delete(`/invoices/${id}/schedule`).then(() => undefined);
}

/** Narrow single-field patch on a draft — unlike updateDraftInvoice, does NOT cancel a pending schedule. */
export function setAutoSendEmailOverride(
  id: string,
  autoSendEmailOverride: boolean
): Promise<ElectronicInvoice> {
  return apiClient
    .patch<ElectronicInvoice>(`/invoices/${id}/auto-send-email`, { autoSendEmailOverride })
    .then((res) => res.data);
}

export function updateDraftInvoice(
  id: string,
  payload: CreateInvoiceRequest
): Promise<ElectronicInvoice> {
  return apiClient
    .patch<ElectronicInvoice>(`/invoices/${id}`, payload)
    .then((res) => res.data);
}

export function deleteDraftInvoice(id: string): Promise<void> {
  return apiClient.delete(`/invoices/${id}`).then(() => undefined);
}

export function createCreditNote(
  invoiceId: string,
  payload: CreateCreditNoteRequest
): Promise<ElectronicInvoice> {
  return apiClient
    .post<ElectronicInvoice>(`/invoices/${invoiceId}/credit-note`, payload)
    .then((res) => res.data);
}

export function createDebitNote(
  invoiceId: string,
  payload: CreateDebitNoteRequest
): Promise<ElectronicInvoice> {
  return apiClient
    .post<ElectronicInvoice>(`/invoices/${invoiceId}/debit-note`, payload)
    .then((res) => res.data);
}

export function sendInvoiceEmail(
  invoiceId: string,
): Promise<{ sent: boolean }> {
  return apiClient
    .post<{ sent: boolean }>(`/invoices/${invoiceId}/send-email`)
    .then((res) => res.data);
}

/** Re-queue DGI submission when the job runner was off or jobs are missing. */
export type QueueInvoiceSubmissionResult = {
  action: "rescheduled" | "enqueued" | "noop";
  jobCount?: number;
};

export function queueInvoiceSubmission(
  invoiceId: string
): Promise<QueueInvoiceSubmissionResult> {
  return apiClient
    .post<QueueInvoiceSubmissionResult>(`/invoices/${invoiceId}/queue-submission`)
    .then((res) => res.data);
}

/**
 * Download invoice PDF. Triggers a file download in the browser.
 */
export function downloadInvoicePdf(invoiceId: string): Promise<void> {
  return apiClient
    .get<Blob>(`/invoices/${invoiceId}/pdf`, { responseType: "blob" })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers) ?? `factura-${invoiceId}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/**
 * Download the Deel-rendered PDF for a Deel-originated invoice. Proxied by
 * the backend through the contractor's Deel token. Only valid when the
 * invoice has createdVia='deel' and a linked deelInvoiceId.
 */
export function downloadDeelInvoicePdf(invoiceId: string): Promise<void> {
  return apiClient
    .get<Blob>(`/integrations/deel/invoices/${invoiceId}/pdf`, {
      responseType: "blob",
    })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers) ?? `deel-${invoiceId}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/**
 * Download invoice XML (signed CFE). Triggers a file download in the browser.
 * Only available for accepted invoices.
 */
export function downloadInvoiceXml(invoiceId: string): Promise<void> {
  return apiClient
    .get<Blob>(`/invoices/${invoiceId}/xml`, { responseType: "blob" })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers) ?? `factura-${invoiceId}.xml`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/**
 * Get preview PDF for draft invoice data. Opens the PDF in a new tab.
 */
export function previewInvoicePdf(payload: CreateInvoiceRequest): Promise<void> {
  return apiClient
    .post<Blob>("/invoices/preview-pdf", payload, { responseType: "blob" })
    .then((res) => {
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    });
}

/** Result of CFE import (emitted or received). Matches backend ImportResult. */
export interface ImportCfeResult {
  imported: number;
  /** Emitted import only: existing invoice refreshed from XML. */
  merged: number;
  skipped: number;
  errors: Array<{ filename: string; message: string; code?: string }>;
}

/**
 * Import emitted CFE XMLs for the current tenant. Use in Settings (Factura Electrónica tab).
 */
export function importEmittedCfes(files: File[]): Promise<ImportCfeResult> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return apiClient
    .post<ImportCfeResult>("/invoices/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

/**
 * Import received CFE XMLs for the current tenant. Use in Settings (Factura Electrónica tab).
 */
export function importRecibidasCfes(files: File[]): Promise<ImportCfeResult> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return apiClient
    .post<ImportCfeResult>("/invoices/recibidas/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export interface SyncRecibidasFromDgiResult {
  processed: number;
  inserted: number;
  updated: number;
}

/** Sync received CFEs from DGI for a date range (tenant; requires prod + DGI certificate). */
export function syncRecibidasFromDgi(params: {
  dateFrom: string;
  dateTo: string;
}): Promise<SyncRecibidasFromDgiResult> {
  return apiClient
    .post<SyncRecibidasFromDgiResult>("/invoices/recibidas/sync", {
      dateFrom: params.dateFrom.slice(0, 10),
      dateTo: params.dateTo.slice(0, 10),
    })
    .then((res) => res.data);
}

/** Download stored CFE XML when the row was created from import (404 if only synced from DGI). */
export async function downloadRecibidaXml(id: string): Promise<void> {
  const response = await apiClient.get<Blob>(`/invoices/recibidas/${id}/xml`, {
    responseType: "blob",
  });
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data as BlobPart], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = `cfe-recibida-${id.slice(0, 8)}.xml`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Download a PDF representation of the received CFE (issuer on the left; no tenant logo). */
export async function downloadRecibidaPdf(id: string): Promise<void> {
  const response = await apiClient.get<Blob>(`/invoices/recibidas/${id}/pdf`, {
    responseType: "blob",
  });
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data as BlobPart], { type: "application/pdf" });
  const disposition = response.headers["content-disposition"];
  let filename = `recibida-${id.slice(0, 8)}.pdf`;
  if (typeof disposition === "string") {
    const match = /filename="([^"]+)"/.exec(disposition);
    if (match?.[1]) filename = match[1];
  }
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function getContentDispositionHeader(
  headers: AxiosResponseHeaders | Record<string, unknown>,
): string | undefined {
  if (
    headers &&
    typeof headers === "object" &&
    "get" in headers &&
    typeof (headers as AxiosResponseHeaders).get === "function"
  ) {
    const v = (headers as AxiosResponseHeaders).get("content-disposition");
    if (typeof v === "string" && v.length > 0) return v;
  }
  const h = headers as Record<string, string | undefined>;
  return h["content-disposition"] ?? h["Content-Disposition"];
}

/** Parse filename from Content-Disposition (RFC 5987 filename* and quoted filename). */
function getDownloadFilenameFromHeaders(headers: AxiosResponseHeaders | Record<string, unknown>): string | null {
  const value = getContentDispositionHeader(headers);
  if (!value) return null;
  const star = value.match(/filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/\+/g, " "));
    } catch {
      // fall through to filename=
    }
  }
  const quoted = value.match(/filename="([^"]+)"/);
  if (quoted?.[1]) return quoted[1];
  const unquoted = value.match(/filename=([^;\s]+)/);
  if (unquoted?.[1]) {
    return unquoted[1].replace(/^["']|["']$/g, "");
  }
  return null;
}

/**
 * Download ZIP of all emitted CFE XMLs for the current tenant. Triggers a file download in the browser.
 * Filename is taken from server (e.g. "CFE Emitidos SALKANTAI SAS.zip").
 */
export function downloadExportEmittedCfes(): Promise<void> {
  return apiClient
    .get<Blob>("/invoices/export/emitted", { responseType: "blob" })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers) ?? "CFE Emitidos.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/**
 * Download ZIP of all received CFE XMLs (that were imported) for the current tenant. Triggers a file download in the browser.
 * Filename is taken from server (e.g. "CFE Recibidos SALKANTAI SAS.zip").
 */
export function downloadExportRecibidasCfes(): Promise<void> {
  return apiClient
    .get<Blob>("/invoices/recibidas/export", { responseType: "blob" })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers) ?? "CFE Recibidos.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/** CAE list item as returned by GET /invoices/cae */
export interface CaeListItem {
  id: string;
  companyId: string;
  cfeType: string;
  series: string;
  rangeFrom: number;
  rangeTo: number;
  validFrom: string;
  validTo: string;
  nextNumber: number;
  authorizationNumber: number | null;
  caeEspecial: number | null;
  causalCaEEsp: number | null;
  lastUsedNumber: number;
  status: "active" | "exhausted" | "expired" | "not_yet_valid";
  expiringSoon: boolean;
  rangeRemaining: number;
  rangeLow: boolean;
  /** ISO date string when CAE was soft-deleted (used for invoices). */
  deletedAt: string | null;
}

export function listCaes(): Promise<CaeListItem[]> {
  return apiClient.get<CaeListItem[]>("/invoices/cae").then((res) => res.data);
}

export function addCae(
  file: File,
  isNew: boolean,
  lastUsedNumber?: number,
  cfeType?: string
): Promise<{ id: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("isNew", String(isNew));
  if (lastUsedNumber !== undefined && lastUsedNumber !== null) {
    formData.append("lastUsedNumber", String(lastUsedNumber));
  }
  if (cfeType !== undefined && cfeType !== "") {
    formData.append("cfeType", cfeType);
  }
  return apiClient
    .post<{ id: string }>("/invoices/cae", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function getCaeXml(id: string): Promise<string> {
  return apiClient
    .get<string>(`/invoices/cae/${id}/xml`, { responseType: "text" })
    .then((res) => res.data);
}

export function downloadCae(id: string): Promise<void> {
  return apiClient
    .get<Blob>(`/invoices/cae/${id}/download`, { responseType: "blob" })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers) ?? `CAE_${id}.xml`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

export function deleteCae(id: string): Promise<void> {
  return apiClient.delete(`/invoices/cae/${id}`).then(() => undefined);
}
